import { useEffect, useId, useRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  readonly label?: ReactNode
  /** Tri-state dash. Driven here (set on the input via ref) so call
   *  sites stay declarative. */
  readonly indeterminate?: boolean
}

/**
 * The single source for interactive checkbox styling — every editable
 * checkbox in the app routes through here, so changing the look here
 * changes it everywhere (mirrors the Input/Button atoms).
 *
 * The native control is suppressed (`appearance-none`, so no
 * browser-default blue) and replaced with a white box that shows the
 * same forest tick as <CheckMark> / the Variant Pricing "Cur" column.
 */
export function Checkbox({
  label,
  className,
  id,
  indeterminate = false,
  disabled = false,
  ...rest
}: CheckboxProps): JSX.Element {
  const reactId = useId()
  const inputId = id ?? reactId
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = indeterminate
  }, [indeterminate])

  const box = (
    <span className={['relative inline-flex h-4 w-4 shrink-0', className ?? ''].filter(Boolean).join(' ')}>
      <input
        {...rest}
        ref={ref}
        id={inputId}
        type="checkbox"
        disabled={disabled}
        className={[
          'peer h-4 w-4 appearance-none rounded border-[1.5px] border-border bg-white transition',
          'checked:border-forest indeterminate:border-forest',
          disabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer focus:ring-2 focus:ring-forest focus:ring-offset-1'
        ]
          .filter(Boolean)
          .join(' ')}
      />
      {/* forest tick — same indicator as <CheckMark> / the Cur column */}
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-bold leading-none text-forest text-[0.7rem] opacity-0 peer-checked:opacity-100">
        ✓
      </span>
      {indeterminate ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-bold leading-none text-forest">
          –
        </span>
      ) : null}
    </span>
  )

  if (label === undefined) return box
  return (
    <label
      htmlFor={inputId}
      className={`inline-flex items-start gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {box}
      <span className="font-body text-body-sm text-text-primary leading-relaxed">{label}</span>
    </label>
  )
}
